import { X, Check, Minus, ListOrdered } from "lucide-react";

// iter87.225 — NBA Series Predictor breakdown, sibling of
// `MlbSeriesBreakdownModal.jsx`. One section per round (First Round /
// Conference Semifinals / Conference Finals / NBA Finals), one row
// per series. Read-only mirror of `nba_engine.compute_nba_series_points`.
export default function NbaSeriesBreakdownModal({ open, onClose, playerName, rounds = [], teamsById = {}, totalPoints = 0 }) {
    if (!open) return null;
    const nameFor = (id) => teamsById[id]?.name || (id ? id : "TBD");
    const roundsWithSeries = rounds.filter((r) => (r.series || []).length > 0);

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
            data-testid="nba-series-breakdown-modal-backdrop"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div
                className="brutal-card max-w-md w-full max-h-[85dvh] flex flex-col"
                data-testid="nba-series-breakdown-modal"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between gap-3 p-5 border-b-2 border-[#222]">
                    <div className="flex items-center gap-2 min-w-0">
                        <ListOrdered className="w-5 h-5 text-volt shrink-0" />
                        <div className="min-w-0">
                            <h2 className="font-display font-black text-lg uppercase truncate">Series Predictor Picks</h2>
                            <p className="text-[11px] text-zinc-500 truncate">{playerName}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="brutal-btn-ghost p-1.5"
                        data-testid="nba-series-breakdown-modal-close-btn"
                        aria-label="Close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="overflow-y-auto px-5 py-3 flex-1 space-y-4" data-testid="nba-series-breakdown-list">
                    {roundsWithSeries.length === 0 && (
                        <p className="py-6 text-center text-xs text-zinc-500" data-testid="nba-series-breakdown-empty">
                            No series picks yet.
                        </p>
                    )}
                    {roundsWithSeries.map((r) => (
                        <div key={r.key} data-testid={`nba-series-breakdown-round-${r.key}`}>
                            <p className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold mb-1.5">{r.label}</p>
                            <div className="space-y-1.5">
                                {(r.series || []).map((s) => {
                                    const picked = s.pick?.team_id;
                                    const hasResult = s.team_right !== null && s.team_right !== undefined;
                                    return (
                                        <div
                                            key={s.id}
                                            data-testid={`nba-series-breakdown-row-${s.id}`}
                                            className="flex items-center gap-2 rounded-lg border border-white/[0.08] bg-white/[0.02] px-3 py-2.5"
                                        >
                                            <div className="flex-1 min-w-0">
                                                <p className="text-xs text-zinc-300 truncate">
                                                    {nameFor(s.team_a_id)} <span className="text-zinc-600">vs</span> {nameFor(s.team_b_id)}
                                                </p>
                                                <p className="text-[10px] text-zinc-500 truncate">
                                                    {picked ? <>Pick: <span className="text-cyber font-semibold">{nameFor(picked)}</span>{s.pick?.game_count ? ` in ${s.pick.game_count}` : ""}</> : "No pick"}
                                                    {s.status === "final" && <> · Final: {nameFor(s.winner_team_id)} won in {s.deciding_game}</>}
                                                </p>
                                            </div>
                                            {hasResult ? (
                                                <div className={`flex items-center gap-1 shrink-0 font-mono font-bold text-sm ${s.team_right ? "text-neon" : "text-heat"}`}>
                                                    {s.team_right ? <Check className="w-3.5 h-3.5" /> : <Minus className="w-3.5 h-3.5" />}
                                                    {s.points > 0 ? `+${s.points}` : s.points}
                                                </div>
                                            ) : (
                                                <span className="text-[10px] text-zinc-600 shrink-0 uppercase">{s.status}</span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="flex items-center justify-between px-5 py-3 border-t-2 border-[#222]">
                    <span className="font-black uppercase tracking-widest text-[10px] text-zinc-400">Total</span>
                    <span className="font-mono font-black text-base text-white" data-testid="nba-series-breakdown-total">
                        {totalPoints > 0 ? `+${totalPoints}` : totalPoints}
                    </span>
                </div>
            </div>
        </div>
    );
}
